import React, { useState } from 'react';
import styled from 'styled-components';

const UploadContainer = styled.div`
  background: #fff;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const UploadTitle = styled.h3`
  font-size: 24px;
  color: #2c5aa0;
  margin-bottom: 20px;
  text-align: center;
`;

const UploadForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2c5aa0;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const FileUploadArea = styled.div<{ isDragOver: boolean }>`
  border: 2px dashed ${props => props.isDragOver ? '#2c5aa0' : '#dee2e6'};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  background: ${props => props.isDragOver ? '#f8f9ff' : '#fafafa'};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: #2c5aa0;
    background: #f8f9ff;
  }
`;

const FileUploadInput = styled.input`
  display: none;
`;

const FileUploadText = styled.div`
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
`;

const FileUploadHint = styled.div`
  font-size: 14px;
  color: #999;
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 10px;
`;

const FileIcon = styled.div`
  font-size: 24px;
`;

const FileInfo = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-weight: 600;
  color: #333;
`;

const FileSize = styled.div`
  font-size: 14px;
  color: #666;
`;

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #c82333;
  }
`;

const UploadButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled ? '#dee2e6' : '#2c5aa0'};
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.3s ease;

  &:hover {
    background: ${props => props.disabled ? '#dee2e6' : '#1e3a8a'};
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin: 10px 0;
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: #2c5aa0;
  transition: width 0.3s ease;
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 16px;
  background: ${props => {
    switch (props.type) {
      case 'success': return '#d4edda';
      case 'error': return '#f8d7da';
      case 'info': return '#d1ecf1';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success': return '#155724';
      case 'error': return '#721c24';
      case 'info': return '#0c5460';
      default: return '#383d41';
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success': return '#c3e6cb';
      case 'error': return '#f5c6cb';
      case 'info': return '#bee5eb';
      default: return '#d6d8db';
    }
  }};
`;

interface UploadResult {
  success: boolean;
  url?: string;
  fileName?: string;
  error?: string;
}

const AdminBulletinUpload: React.FC = () => {
  const [bulletinTitle, setBulletinTitle] = useState('');
  const [bulletinDate, setBulletinDate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const handleFileSelect = (file: File) => {
    // 파일 타입 검증
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setUploadResult({
        success: false,
        error: 'JPG, PNG, PDF 파일만 업로드 가능합니다.'
      });
      return;
    }

    // 파일 크기 검증 (10MB for PDFs, 5MB for images)
    const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadResult({
        success: false,
        error: file.type === 'application/pdf'
          ? 'PDF 파일 크기는 10MB 이하여야 합니다.'
          : '이미지 파일 크기는 5MB 이하여야 합니다.'
      });
      return;
    }

    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadResult(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type === 'application/pdf') return '📄';
    return '🖼️';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedFile || !bulletinTitle.trim() || !bulletinDate.trim()) {
      setUploadResult({
        success: false,
        error: '제목, 날짜, 파일을 모두 입력해주세요.'
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile); // Single file (PDF or image)
      formData.append('title', bulletinTitle);
      formData.append('date', bulletinDate);

      // 진행률 시뮬레이션
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
      const apiKey = localStorage.getItem('adminToken') || process.env.REACT_APP_ADMIN_API_KEY || '';

      const response = await fetch(`${backendUrl}/api/upload-bulletin`, {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const result = await response.json();

      if (result.success) {
        setUploadResult({
          success: true,
          url: result.url,
          fileName: result.fileName
        });

        // 폼 초기화
        setBulletinTitle('');
        setBulletinDate('');
        setSelectedFile(null);
      } else {
        setUploadResult({
          success: false,
          error: result.error || '업로드에 실패했습니다.'
        });
      }
    } catch (error) {
      setUploadResult({
        success: false,
        error: '서버 연결에 실패했습니다.'
      });
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  return (
    <UploadContainer>
      <UploadTitle>📤 주보 업로드</UploadTitle>

      <UploadForm onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="bulletinTitle">주보 제목</Label>
          <Input
            id="bulletinTitle"
            type="text"
            value={bulletinTitle}
            onChange={(e) => setBulletinTitle(e.target.value)}
            placeholder="예: 제280호 주보"
            disabled={isUploading}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="bulletinDate">주보 날짜</Label>
          <Input
            id="bulletinDate"
            type="date"
            value={bulletinDate}
            onChange={(e) => setBulletinDate(e.target.value)}
            disabled={isUploading}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>주보 파일 (JPG, PNG, PDF)</Label>
          <FileUploadArea
            isDragOver={isDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('fileInput')?.click()}
          >
            <FileUploadInput
              id="fileInput"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <FileUploadText>
              클릭하거나 파일을 드래그하여 업로드
            </FileUploadText>
            <FileUploadHint>
              JPG, PNG, PDF 파일 (이미지: 최대 5MB, PDF: 최대 10MB)<br />
              💡 PDF 파일이 권장됩니다
            </FileUploadHint>
          </FileUploadArea>

          {selectedFile && (
            <SelectedFile>
              <FileIcon>{getFileIcon(selectedFile)}</FileIcon>
              <FileInfo>
                <FileName>{selectedFile.name}</FileName>
                <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
              </FileInfo>
              <RemoveButton type="button" onClick={removeFile}>
                제거
              </RemoveButton>
            </SelectedFile>
          )}
        </FormGroup>

        {isUploading && (
          <div>
            <ProgressBar>
              <ProgressFill progress={uploadProgress} />
            </ProgressBar>
            <div style={{ textAlign: 'center', color: '#666' }}>
              업로드 중... {uploadProgress}%
            </div>
          </div>
        )}

        <UploadButton
          type="submit"
          disabled={!selectedFile || !bulletinTitle.trim() || !bulletinDate.trim() || isUploading}
        >
          {isUploading ? '업로드 중...' : '주보 업로드'}
        </UploadButton>

        {uploadResult && (
          <StatusMessage type={uploadResult.success ? 'success' : 'error'}>
            {uploadResult.success ? (
              <div>
                ✅ 주보가 성공적으로 업로드되었습니다!<br />
                <strong>URL:</strong> <a href={uploadResult.url} target="_blank" rel="noopener noreferrer">
                  {uploadResult.url}
                </a>
              </div>
            ) : (
              <div>❌ {uploadResult.error}</div>
            )}
          </StatusMessage>
        )}
      </UploadForm>
    </UploadContainer>
  );
};

export default AdminBulletinUpload;
